import { useEffect, useRef, useState } from "react";
import { Loader2, Megaphone, Stethoscope, Volume2, VolumeX } from "lucide-react";
import { api } from "@/lib/api";

export default function QueueDisplay() {
    const [data, setData] = useState(null);
    const [now, setNow] = useState(new Date());
    const [soundOn, setSoundOn] = useState(false);
    const soundRef = useRef(false);
    const prevServing = useRef({});

    const playDing = () => {
        try {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            const ctx = new Ctx();
            [880, 1174.66].forEach((freq, i) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = "sine";
                osc.frequency.value = freq;
                const t = ctx.currentTime + i * 0.18;
                gain.gain.setValueAtTime(0.0001, t);
                gain.gain.exponentialRampToValueAtTime(0.35, t + 0.03);
                gain.gain.exponentialRampToValueAtTime(0.0001, t + 1.1);
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.start(t);
                osc.stop(t + 1.2);
            });
        } catch { /* audio unsupported */ }
    };

    const announce = (docName, tok) => {
        playDing();
        if (!("speechSynthesis" in window)) return;
        try {
            window.speechSynthesis.cancel();
            const u = new SpeechSynthesisUtterance(
                `Token number ${tok.token_no}. ${tok.patient_name}. Please proceed to ${docName}.`
            );
            u.lang = "en-IN";
            u.rate = 0.92;
            window.speechSynthesis.speak(u);
        } catch { /* tts unsupported */ }
    };

    const load = async () => {
        try {
            const { data } = await api.get("/queue/summary");
            if (soundRef.current) {
                (data.doctors || []).forEach((d) => {
                    const key = d.doctor_id || "any";
                    const cur = d.now_serving;
                    const prev = prevServing.current[key];
                    if (cur && cur.token_no !== prev) {
                        prevServing.current[key] = cur.token_no;
                        announce(d.doctor_name, cur);
                    } else if (!cur && prev) {
                        prevServing.current[key] = null;
                    }
                });
            } else {
                (data.doctors || []).forEach((d) => {
                    prevServing.current[d.doctor_id || "any"] = d.now_serving ? d.now_serving.token_no : null;
                });
            }
            setData(data);
        } catch { /* keep last good frame */ }
    };

    useEffect(() => {
        load();
        const poll = setInterval(load, 10000);
        const clock = setInterval(() => setNow(new Date()), 1000);
        return () => { clearInterval(poll); clearInterval(clock); };
    }, []);

    const enableSound = () => {
        soundRef.current = true;
        setSoundOn(true);
        playDing();
    };

    const disableSound = () => {
        soundRef.current = false;
        setSoundOn(false);
        if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    };

    const doctors = data?.doctors || [];

    return (
        <div className="min-h-screen bg-[#0F172A] text-white flex flex-col" data-testid="queue-display-page">
            <div className="flex items-center justify-between px-10 py-6 border-b border-white/10">
                <div className="flex items-center gap-4">
                    <img src="/zen-logo.webp" alt="Zen Hospital" className="h-12 w-auto bg-white rounded-lg px-2 py-1" />
                    <div>
                        <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#F6C9A4]">Zen Hospital · Women's Clinic & Birthing Centre</p>
                        <h1 className="font-heading text-2xl font-extrabold tracking-tight">OPD Token Display</h1>
                    </div>
                </div>
                <div className="flex items-center gap-6">
                    {soundOn ? (
                        <button data-testid="sound-off-btn" onClick={disableSound} className="flex items-center gap-2 rounded-full bg-emerald-500/20 border border-emerald-400/40 px-4 py-2 text-sm font-semibold text-emerald-300 hover:bg-emerald-500/30 transition-colors">
                            <Volume2 className="h-4 w-4" /> Sound On
                        </button>
                    ) : (
                        <button data-testid="sound-on-btn" onClick={enableSound} className="flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-4 py-2 text-sm font-semibold text-slate-300 hover:bg-white/20 transition-colors animate-pulse">
                            <VolumeX className="h-4 w-4" /> Enable Sound
                        </button>
                    )}
                    <div className="text-right">
                        <p className="font-mono2 text-4xl font-bold tabular-nums" data-testid="display-clock">{now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</p>
                        <p className="text-sm text-slate-400">{now.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
                    </div>
                </div>
            </div>

            {!data ? (
                <div className="flex-1 flex items-center justify-center"><Loader2 className="h-10 w-10 animate-spin text-[#E06D1F]" /></div>
            ) : doctors.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center gap-3" data-testid="display-empty">
                    <Stethoscope className="h-14 w-14 text-slate-600" />
                    <p className="text-2xl text-slate-400">Queue not started yet — please check in at the front desk</p>
                </div>
            ) : (
                <div className={`flex-1 grid gap-6 p-8 ${doctors.length === 1 ? "grid-cols-1" : doctors.length === 2 ? "grid-cols-2" : "grid-cols-2 xl:grid-cols-3"}`}>
                    {doctors.map((d) => (
                        <div key={d.doctor_id || "any"} data-testid={`display-doctor-${d.doctor_id || "any"}`} className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur flex flex-col overflow-hidden">
                            <div className="px-8 py-5 border-b border-white/10 flex items-center justify-between">
                                <p className="text-xl font-bold text-slate-100">{d.doctor_name}</p>
                                <span className="rounded-full bg-white/10 px-3 py-1 text-sm text-slate-300">{d.waiting} waiting</span>
                            </div>
                            <div className="flex-1 flex flex-col items-center justify-center py-10 px-6">
                                {d.now_serving ? (
                                    <>
                                        <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.3em] text-[#F6C9A4]">
                                            <Megaphone className="h-4 w-4" /> {d.now_serving.status === "called" ? "Now Calling" : "Now Serving"}
                                        </p>
                                        <p className="font-heading text-8xl font-extrabold tracking-tight text-[#E06D1F] mt-3 animate-pulse" data-testid={`display-token-${d.doctor_id || "any"}`}>#{d.now_serving.token_no}</p>
                                        <p className="mt-3 text-3xl font-semibold text-white">{d.now_serving.patient_name}</p>
                                    </>
                                ) : (
                                    <>
                                        <p className="text-sm font-bold uppercase tracking-[0.3em] text-slate-500">Please Wait</p>
                                        <p className="font-heading text-5xl font-extrabold text-slate-600 mt-4">—</p>
                                        <p className="mt-3 text-lg text-slate-500">Token will be called shortly</p>
                                    </>
                                )}
                            </div>
                            <div className="px-8 py-4 border-t border-white/10 bg-black/20 flex items-center justify-between">
                                <p className="text-sm text-slate-400">{d.up_next ? <>Up next: <b className="text-slate-200">#{d.up_next.token_no} {d.up_next.patient_name}</b></> : "No one in line"}</p>
                                <p className="text-sm text-slate-500">{d.done} done today</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="px-10 py-4 border-t border-white/10 flex items-center justify-between">
                <p className="text-xs text-slate-500">Please keep your token number handy · In case of emergency, inform the front desk immediately</p>
                <p className="text-xs text-slate-500">Ph: +91 9099347456</p>
            </div>
        </div>
    );
}
